import { useCallback, useEffect, useRef, useState } from 'react';
import { notify } from '../../../notifications';
import { apiErrorMessage } from '../../reports/edit/editApi';
import {
  addOverrides, clearNetworkRule, deleteOverride, fetchEntry, saveSchedule, setNetworkRule
} from './revenueEntryApi';
import type { EntryData, EntryKind, EntryOverride, EntryRuleInput, EntrySchedule } from './revenueEntryApi';
import { refreshDelay } from './revenueEntryCalc';

export type EntryAction = 'schedule' | 'network' | 'clear' | 'overrides' | 'remove';

/** Границы форм (текущее время, 24 часа) считает сервер: перед открытием формы данные старше минуты перечитываются */
const FRESH_MS = 60 * 1000;

/** Данные экрана «Окно ввода»: параметры сети, исключения аптек и их изменение */
export function useRevenueEntry() {
  const [data, setData] = useState<EntryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<EntryAction | null>(null);

  const requestRef = useRef(0);
  const pendingRef = useRef(false);
  const loadedAtRef = useRef(0);

  /** keepRows: после действия на экране остаются прежние значения (приглушенные), а не пустой экран */
  const load = useCallback(async (keepRows: boolean): Promise<boolean> => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError('');
    if (!keepRows) setData(null);

    try {
      const next = await fetchEntry();
      // Устаревший ответ (экран закрыт или данные уже перечитаны) отбрасывается
      if (requestId !== requestRef.current) return false;
      loadedAtRef.current = Date.now();
      setData(next);
      return true;
    } catch (err) {
      if (requestId !== requestRef.current) return false;
      const reason = apiErrorMessage(err, 'Проверьте соединение и повторите попытку');
      if (keepRows) notify(`Не удалось обновить окно ввода. ${reason}`, 'error');
      else setError(reason);
      return false;
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    []
  );

  // Когда истекает срок правила или наступает закрытие/открытие ввода, значения на экране перечитываются
  useEffect(() => {
    if (!data) return undefined;
    const delay = refreshDelay(data, loadedAtRef.current);
    if (delay === null) return undefined;
    const timer = window.setTimeout(() => {
      load(true);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [data, load]);

  const retry = useCallback(() => {
    load(false);
  }, [load]);

  /** Перед открытием формы: true, если данные свежие или успешно перечитаны */
  const ensureFresh = useCallback(async (): Promise<boolean> => {
    if (Date.now() - loadedAtRef.current < FRESH_MS) return true;
    return load(true);
  }, [load]);

  /** Выполняет изменение и перечитывает данные: после сбоя состояние на сервере могло измениться */
  const run = useCallback(
    async (action: EntryAction, task: () => Promise<string>, failText: string): Promise<boolean> => {
      if (pendingRef.current) return false;
      pendingRef.current = true;
      setPending(action);

      let done = false;
      try {
        notify(await task(), 'success');
        done = true;
      } catch (err) {
        notify(apiErrorMessage(err, failText), 'error');
      } finally {
        pendingRef.current = false;
        setPending(null);
      }

      load(true);
      return done;
    },
    [load]
  );

  const changeSchedule = useCallback(
    (schedule: EntrySchedule) => run('schedule', () => saveSchedule(schedule), 'Не удалось сохранить окно ввода'),
    [run]
  );

  const setNetwork = useCallback(
    (kind: EntryKind, input: EntryRuleInput, confirm?: string) =>
      run('network', () => setNetworkRule(kind, input, confirm), 'Не удалось сохранить параметр'),
    [run]
  );

  const clearNetwork = useCallback(
    (kind: EntryKind) =>
      run('clear', async () => (await clearNetworkRule(kind)).message, 'Не удалось отменить параметр'),
    [run]
  );

  const saveOverrides = useCallback(
    (pharmacies: string[], kind: EntryKind, input: EntryRuleInput) =>
      run('overrides', () => addOverrides(pharmacies, kind, input), 'Не удалось сохранить исключения'),
    [run]
  );

  const removeOverride = useCallback(
    (item: EntryOverride) => run('remove', () => deleteOverride(item.id), 'Не удалось отменить исключение'),
    [run]
  );

  return {
    data, loading, error, retry, pending, ensureFresh,
    changeSchedule, setNetwork, clearNetwork, saveOverrides, removeOverride
  };
}
